import ActionButton from '@jimmyboys/liita-ui/dist/components/ActionButton'
import { useGlobal } from '@jimmyboys/liita-ui/dist/components/GlobalContext'
import TaskHeaderErrors from 'components/TaskHeaderErrors/TaskHeaderErrors'
import { useEffect, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'

import styles from './../../Dialog.module.sass'
import { useFetchList } from 'store'
import { useDeepCompareEffect } from 'react-use'
import { zodResolver } from '@hookform/resolvers/zod'
import { EditAppointmentProtocolSchema } from './schemas/EditAppointmentProtocolSchema'
import { flatHookFormErrors } from 'controllers/TaskManager/controllers/utils'
import { API_URL, fetchOptions, parseJSONContent } from 'store/fetchUtils'
import { translateErrorMessage } from 'hooks/useSubmitTask'
import useSession from 'hooks/useSession'
import HFAppointmentProtocol from 'components/HFInputs/HFAppointmentProtocol'
import { useReload } from 'store'
import { Appointment, AppointmentProtocolRule, Options } from 'models'

const EditAppointmentProtocolDialog = (props) => {
	const { data, dismiss, resolve } = props
	const { processId } = data
	const { i18n, pushNotification } = useGlobal()
	const { logout } = useSession()
	const reload = useReload()
	const [submitting, setSubmitting] = useState(false)
	const [error, setError] = useState(null)

	const methods = useForm({
		mode: 'onSubmit',
		resolver: zodResolver(EditAppointmentProtocolSchema),
		defaultValues: {
			appointments: [
				{
					quantity: 1,
					specialtyId: '',
					serviceProviderId: '',
				},
			],
		},
	})

	const { handleSubmit, formState, reset } = methods

	const medicalSpecialtiesUri = '/metadata/medical_specialties'
	const appointmentProcessProtocolsUri = `/rules/clinical_process/${processId}?ruleType=AutomationRule&taskType=AppointmentClinicalApproval`
	const appointmentsUri = `/clinical_processes/${processId}/appointments`
	const processTasksUri = `/clinical_processes/${processId}/tasks`

	const {
		contents: [
			medicalSpecialties,
			appointments,
			appointmentProcessProtocols,
		],
	} = useFetchList(
		[
			medicalSpecialtiesUri,
			appointmentsUri,
			appointmentProcessProtocolsUri,
		],
		[Options.fromSimpleObjectJSON, Appointment.fromJSON]
	)

	const onSubmit = (formData) => {
		if (submitting) {
			return
		}

		const { appointments } = formData

		const endpoint = `${API_URL}/api/rules/clinical_process/${processId}`

		const filteredAppointments = appointments?.filter(
			({ specialtyId }) => specialtyId !== ''
		)

		const appointmentsRequestBody =
			AppointmentProtocolRule.constructRequestBody(
				filteredAppointments,
				`Automação protocolo para processo: ${processId}`
			)

		setError(null)
		setSubmitting('submitting')

		return fetch(
			endpoint,
			fetchOptions({
				method: 'PUT',
				body: appointmentsRequestBody,
			})
		)
			.then((r) => parseJSONContent(r))
			.then(() => {
				resolve && resolve()
				reload([appointmentProcessProtocolsUri, processTasksUri])
				pushNotification(
					undefined,
					'success',
					i18n`Process updated successfully!`
				)
			})
			.catch((e) => {
				if (e.status === '401' || e.status === 401) {
					logout()
				}

				const errorMessage = translateErrorMessage(e, i18n)

				setSubmitting(false)
				setError([errorMessage])
			})
	}

	useDeepCompareEffect(() => {
		if (appointmentProcessProtocols?.length) {
			const appointmentsRuleValuesObj =
				AppointmentProtocolRule.retrieveRuleValues(
					appointmentProcessProtocols
				)

			reset({
				appointments: appointmentsRuleValuesObj,
			})
		}
	}, [appointmentProcessProtocols || [], reset])

	useEffect(() => {
		const flattenErrors = flatHookFormErrors(formState.errors)

		if (flattenErrors) {
			const errorMessages = Object.keys(flattenErrors).map((errorKey) => {
				const errorMessage = flattenErrors[errorKey]
				const splittedErrorKey = errorKey.split('.')
				const field = splittedErrorKey[splittedErrorKey.length - 2]

				return `${i18n.translate(field)}: ${i18n.translate(
					errorMessage
				)}`
			})
			setError(errorMessages)
		}
	}, [formState.errors, i18n])

	return (
		<>
			<TaskHeaderErrors errors={error ?? []} />
			<FormProvider {...methods}>
				<form
					id={'form_process_protocol_edit'}
					onSubmit={handleSubmit(onSubmit)}
					className={`${styles['protocols-form'] || ''}`}
					disabled={submitting}
				>
					<fieldset>
						<HFAppointmentProtocol
							data={{
								medicalSpecialties,
								appointments,
								processId,
							}}
							name={'appointments'}
						/>
					</fieldset>
				</form>
			</FormProvider>

			<menu>
				<ActionButton type='button' secondary onClick={dismiss}>
					<small>
						<strong>{i18n`Cancel`}</strong>
					</small>
				</ActionButton>
				<ActionButton form={`form_process_protocol_edit`} primary>
					<small>
						<strong>
							{submitting ? i18n`Submitting` : i18n`Save`}
						</strong>
					</small>
				</ActionButton>
			</menu>
		</>
	)
}

export default EditAppointmentProtocolDialog
